import { SWEEP_SPEED } from './pulseModel.js';

// Sample from elapsed time instead of accumulating frame-dependent pixel steps.
// Each instance owns its display; resize and remount cannot retain stale history.
class Monitor {
  constructor({ sample, color, sampleStep = 1 }) {
    this.sample = sample;
    this.color = color;
    this.sampleStep = sampleStep;
  }

  plot(context, width, height, seconds) {
    context.clearRect(0, 0, width, height);
    if (width <= 2) return;

    const head = (seconds * SWEEP_SPEED) % width;
    const eraseWidth = Math.min(12, width / 10);
    const trace = [];
    const tip = [];
    // Decay in elapsed seconds, so phosphor persistence looks the same on
    // different screen widths and at different animation frame rates.
    const gradient = context.createLinearGradient(0, 0, width, 0);
    const stops = [0, width, head, Math.min(width, head + eraseWidth)];
    [12, 40, 100, 250, 500].forEach((distance) => {
      stops.push((head - (distance % width) + width) % width);
    });
    stops.sort((a, b) => a - b).forEach((x) => {
      const age = ((head - x + width) % width) / SWEEP_SPEED;
      const brightness = 0.28 + 0.72 * Math.exp(-age / 3);
      gradient.addColorStop(x / width, `rgba(${this.color.join(',')},${brightness})`);
    });
    context.lineJoin = 'round';
    context.lineCap = 'round';

    for (let x = 1; x < width - 1; x += this.sampleStep) {
      const age = (head - x + width) % width;
      if (age > width - eraseWidth) {
        trace.push(null);
        tip.push(null);
        continue;
      }
      const time = seconds - age / SWEEP_SPEED;
      const point = { x, y: this.sample(time) };
      trace.push(point);
      tip.push(age < 12 ? point : null);
    }

    const drawPath = (points) => {
      context.beginPath();
      let connected = false;
      points.forEach((point) => {
        if (!point) connected = false;
        else {
          if (connected) context.lineTo(point.x, point.y);
          else context.moveTo(point.x, point.y);
          connected = true;
        }
      });
    };
    // Reuse one path for the soft phosphor halo and its sharp signal core.
    // The erase gap stays empty even when the sweep wraps around the canvas.
    drawPath(trace);
    context.strokeStyle = gradient;
    context.globalAlpha = 0.13;
    context.lineWidth = 4.5;
    context.stroke();
    context.globalAlpha = 1;
    context.lineWidth = 1.5;
    context.stroke();

    drawPath(tip);
    const hotColor = this.color.map((channel) => Math.round(channel + (255 - channel) * 0.5));
    context.strokeStyle = `rgba(${hotColor.join(',')},0.85)`;
    context.lineWidth = 1.7;
    context.stroke();
  }
}

export default Monitor;
